import React from 'react';
import { MantineProvider, v8CssVariablesResolver } from '@mantine/core';
import { MantineEmotionProvider, emotionTransform } from '@mantine/emotion';
import { autorun, computed, observable } from 'mobx';
import SeedlangDesktopHeader from 'components/layout/desktop/seedlang_header';
import MemrevHeader from 'components/layout/desktop/memrev_header';
import DesktopFooter from 'components/layout/desktop/footer';
import MobileHeader from 'components/layout/mobile/header';
import MobileFooter from 'components/layout/mobile/footer';
import { observer } from 'mobx-react';
import { last, some, noop } from 'lodash';
import { AppUI, TreeUI, VocabUI } from '@seedlang/state';
import OverlayWrapper from 'components/layout/overlay_wrapper';
import ServerAlert from 'components/server_alert';
import { Constants, Theme } from '@seedlang/constants';
import { isPresent, loadCss, sentryInitializer } from '@seedlang/utils';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import Cookies from 'js-cookie';
import Spinner from 'components/spinner';
import CookieAgreement from 'components/cookie_agreement';
import MissingAgreement from 'components/missing_agreement';
import UpdatedTermsModal from 'components/updated_terms_modal';
import Link from 'components/link';
import BackToTopButton from 'components/button/back_to_top_button';
import { pixify } from '@seedlang/utils/src';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import BannerNoticeIndex from 'components/banner_notice/banner_notice_index';
import MobileOnly from 'utils/mobile_only';
import { mantineTheme } from 'pages/creator/mantine_theme';
import { Outlet } from 'react-router-dom';
import '../mantine_component_styles';

const PAGE_SCROLL_ROUTES = ['help_center', 'faq_categories.show', 'faq_items.show'];

const Wrapper = styled.div`
  width: ${props => props.width};
  overflow: ${props => props.overflow};
  visibility: ${props => props.visibility};
  flex: 1;
  display: ${props => props.display};
  flex-direction: column;
  align-items: center;
  position: relative;
  min-height: 100vh;
  z-index: 99;
  .primary-page-onboarding {
    i {
      position: absolute;
      top: 8px;
      right: 8px;
      font-size: 20px;
      cursor: pointer;
    }
  }
  h2 {
    font-size: 26px;
    line-height: 26px;
    font-weight: bold;
    margin: 10px 0;
    color: #333;
  }
  h3 {
    font-size: 20px;
    line-height: 20px;
    font-weight: bold;
    margin: 10px 0;
    color: #333;
  }
  #appleid-signin {
    background: #FFF;
    border-radius: 50px;
  }
  #appleid-signin > div > div {
    padding: 4px;
  }
`;

const MainContainer = styled.div`
  max-width: ${props => props.maxWidth};
  padding: ${props => props.padding};
  overflow: ${props => props.overflow};
  width: 100%;
  flex: 1;
  -webkit-overflow-scrolling: touch;
  padding-bottom: ${props => props.mobileFooterVisible ? '50px' : null};
  box-sizing: border-box;
  scrollbar-width: ${props => props.isMobile ? 'none' : null};
  &::-webkit-scrollbar {
    z-index: 99;
    display: ${props => props.isMobile ? 'none' : null};
  }
`;

const Localhost = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 10px;
  color: white;
  font-size: 9px;
  background: #FF5252;
  text-align: center;
  line-height: 10px;
  font-weight: bold;
  z-index: 99;
  div {
    cursor: pointer;
  }
`;

const MigrationBanner = styled.div`
  top: 70px;
  height: fit-content;
  background: #AFD6E7;
  width: 100vw;
  position: relative;
  text-align: center;
  font-size: 14px;
  line-height: 18px;
  padding: 5px 10px;
  @media only screen and (max-width : 649px) {
    top: 0;
  }
`;

@observer
class App extends React.Component {
  @observable showLocalhostBar = true;
  @observable previousRouteName;
  @observable userIsLoaded = false;
  @observable showBackToTop = false;

  @computed get adminPage() {
    return AppUI.routeStore.routeName?.match('builder') || AppUI.routeStore.routeName?.match('creator');
  }

  routeContainsString(ary) {
    return AppUI.routeStore.routeName && ary.map(item => isPresent(AppUI.routeStore.routeName.match(item))).find(item => item);
  }

  get currentRoutes() {
    return AppUI.routeStore.routes && AppUI.routeStore.routes.length ? AppUI.routeStore.routes : (this.props.routes || []);
  }

  get currentRoute() {
    return last(this.currentRoutes) || {};
  }

  get currentRouteIsAdminArea() {
    return this.currentRoutes.some(route => ['builder', 'creator'].includes(route.name));
  }

  get currentRouteName() {
    return this.currentRoute.name || AppUI.routeStore.routeName;
  }

  @computed get deckRoute() {
    return AppUI.routeStore.routeName && !AppUI.routeStore.routeName.match('translator.decks_show') && (AppUI.routeStore.routeName.match('decks.show') || AppUI.routeStore.routeName.match('decksWithOpponent.show'));
  }

  @computed get hideHeader() {
    const routeName = this.currentRouteName || '';
    return this.deckRoute || routeName.match('onboarding') || routeName.match('unpublished') || Constants.HIDE_HEADER_ROUTES.includes(routeName);
  }

  @computed get showFullWidth() {
    return this.loadingLayout
      || this.triviaHomeLoadingLayout
      || this.desktopLandingLayout
      || this.maintenanceLayout
      || this.currentRouteName === 'survey'
      || this.showPageFullWidth;
  }

  @computed get loadingLayout() {
    return AppUI.loadingUser;
  }

  @computed get triviaHomeLoadingLayout() {
    return AppUI.routeStore.routeName === 'trivia.home' && !AppUI.userIsLoaded;
  }

  @computed get desktopLandingLayout() {
    return this.routeContainsString(['landing']) && !AppUI.layout.isMobile;
  }

  @computed get maintenanceLayout() {
    return AppUI.routeStore.routeName === 'maintenance';
  }

  @computed get showPageFullWidth() {
    return this.currentRouteName === 'page.show' && AppUI.pageStore.showData?.fullWidth;
  }

  @computed get defaultSiteMobileHeaderLayout() {
    return this.useMobileHeader && AppUI.siteIsSeedlang;
  }

  @computed get mainContainerPadding() {
    if (this.loadingLayout
      || this.triviaHomeLoadingLayout
      || this.desktopLandingLayout
      || this.maintenanceLayout
      || this.defaultSiteMobileHeaderLayout
      || AppUI.layout.isMobile
      || this.hideHeader) {
      return null;
    }

    return '80px 10px 0 10px';
  }

  @computed get mainContainerOverflow() {
    if (this.pageScrollRoute) {
      return null;
    }
    return AppUI.layout.isMobile
      && AppUI.siteIsSeedlang
      && !['reviews.decks', 'reviews.home', 'reviews.archive', 'reviews.index', 'reviews.create'].includes(AppUI.routeStore.routeName) ? 'auto' : null;
  }

  @computed get pageScrollRoute() {
    return PAGE_SCROLL_ROUTES.includes(this.currentRouteName);
  }

  @computed get wrapperOverflow() {
    if (this.pageScrollRoute) {
      return null;
    }
    return AppUI.layout.isMobile && Constants.HIDE_HEADER_ROUTES.indexOf(AppUI.routeStore.routeName) ? null : 'auto';
  }

  @computed get hideFooter() {
    const routeName = this.currentRouteName || '';
    return this.deckRoute || routeName.match('unpublished') || (AppUI.layout.isMobile && AppUI.user.signedOut) || Constants.HIDE_FOOTER_ROUTES.includes(routeName) || (AppUI.layout.isMobile && !AppUI.routeStore.routeName);
  }

  @computed get showMobileFooter() {
    return AppUI.siteIsSeedlang && AppUI.layout.isMobile && this.currentRouteName !== 'onboarding' && !this.hideFooter;
  }

  @computed get showLandingPage() {
    return !AppUI.user.signedIn && AppUI.routeStore.routeName === 'landing';
  }

  get landingRouteShouldRedirect() {
    return this.userIsLoaded
      && this.currentRouteName === 'landing'
      && (AppUI.userIsSignedIn || AppUI.siteIsMemrev);
  }

  @computed get memrevHomeRoute() {
    return this.homeRouteForSite(AppUI.site) || 'worksheets.index';
  }

  homeRouteForSite = (site) => {
    if (AppUI.userIsSignedIn) {
      return site?.signedInHomePageRoute || site?.signed_in_home_page_route;
    }
    return site?.signedOutHomePageRoute || site?.signed_out_home_page_route;
  }

  redirectSignedOutFromProtectedRoute = () => {
    const routeName = this.currentRouteName;
    if (!routeName || !AppUI.user.signedOut) return;

    if (AppUI.isUnpublishedAccessBlocked && !['onboarding', 'onboarding.sign_in', 'unpublished', 'access', 'onboarding.patreon'].includes(routeName)) {
      AppUI.routeStore.routeToNamed('unpublished');
    } else if (this.currentRouteIsAdminArea) {
      AppUI.routeStore.routeToNamed('onboarding.sign_in');
    } else if (!this.currentRoute.signedOutRoute && AppUI.siteIsSeedlang) {
      AppUI.routeStore.routeToNamed('landing');
    }
  }

  @computed get siteId() {
    return Cookies.get('site_id');
  }

  constructor(props) {
    super(props);
    this.appRef = React.createRef();
    sentryInitializer();
    this.loadConditionalIncludes().catch(error => {
      Sentry.captureException(error);
    });

    this.loadUser();
    if (AppUI.siteIsMemrev && AppUI.layout.isMobile) {
      document.body.style.height = 'auto';
      document.documentElement.style.height = 'auto';
      document.documentElement.style.overflow = 'auto';
    }
    if (!AppUI.layout.isNative) {
      AppUI.soundEffectStore.loadAudio();
    }
    AppUI.detectDataSaver();
    const routeName = this.currentRouteName;
    if (routeName) {
      AppUI.routeStore.setRouteName(routeName);
      this.previousRouteName = routeName;
    }
    if (window.location.search.match('clicked_mailing_id')) {
      const ary = window.location.search.split('?clicked_mailing_id=');
      if (ary[1]) {
        AppUI.campaignStore.trackClicked({ ids: { campaignId: ary[1] } }, noop);
      }
    }
    autorun(() => {
      if (AppUI.targetLanguageId && AppUI.targetLanguageId !== VocabUI.targetLanguageId) {
        VocabUI.onSetTargetLanguageId(AppUI.targetLanguageId);
      }
    });
  }

  loadConditionalIncludes = async () => {
    if (AppUI.siteIsSeedlang) {
      if (process.env.NODE_ENV === 'development') {
        loadCss('https://cloud.typography.com/7576076/6435012/css/fonts.css');
      } else {
        loadCss('https://cloud.typography.com/7576076/6235012/css/fonts.css');
      }
    } else {
      await Promise.all([
        import('../css/membership_platform.css'),
        import('../css/karla-font.css'),
        import('../css/rubik-font.css'),
        import('../css/noto-sans-font.css'),
      ]);
    }
  }

  loadUser = () => {
    AppUI.loadUser(this.afterLoadUser);
  }

  afterLoadUser = (resp) => {
    AppUI.setAuthUserStoreShowData(resp);
    if (AppUI.routeStore.routeName === 'landing') {
      const site = resp?.site || AppUI.site;
      const siteIsSeedlang = site ? (site.isSeedlang ?? site.is_seedlang) : AppUI.siteIsSeedlang;

      if (!siteIsSeedlang) {
        const homeRoute = this.homeRouteForSite(site);
        AppUI.routeStore.routeToNamed(homeRoute || 'worksheets.index');
      } else if (AppUI.userIsSignedIn) {
        AppUI.routeToHome();
      }
    }
    this.userIsLoaded = true;
    this.redirectSignedOutFromProtectedRoute();
    if (AppUI.siteIsSeedlang && !(AppUI.routeStore.routeName.startsWith('builder') || AppUI.routeStore.routeName.startsWith('creator'))) {
      AppUI.loadReviewDecks();
      TreeUI.loadTreesFromTargetLanguage(AppUI.targetLanguageId);
    }
    Sentry.setUser({ id: AppUI.user.id, email: AppUI.user.email });
    if (AppUI.user.siteShowsMaintenancePage) {
      AppUI.routeStore.routeToNamed('maintenance');
    }
    if (isPresent(AppUI.user.contractedModules)) {
      AppUI.updateContractedModulesAry(AppUI.user.contractedModules.split(','));
    }
    if (AppUI.siteIsMemrev) {
      this.loadMemrevFonts();
    }
  }

  loadMemrevFonts = () => {
    if (AppUI.user.site.headerFont) {
      document.body.style.setProperty(
        '--header-font',
        AppUI.user.site.headerFont
          .replace('header_', '')
          .replace(/_/g, ' '),
      );
    }

    if (AppUI.user.site.bodyFont) {
      document.body.style.setProperty(
        '--body-font',
        AppUI.user.site.bodyFont
          .replace('body_', '')
          .replace(/_/g, ' '),
      );
    }
  }

  onScroll = () => {
    if (!AppUI.pageHasScrolled) {
      AppUI.set('pageHasScrolled', true);
    }
    if (AppUI.routeStore.routeName === 'stories.home' && this.appRef.current) {
      const scrollPosition = this.appRef.current.scrollTop;
      AppUI.set('contentTreeScrollPosition', scrollPosition);
    }
    if (['videos.index', 'podcast.index', 'worksheets.index'].includes(AppUI.routeStore.routeName) && this.appRef.current) {
      const scrollPosition = this.appRef.current.scrollTop;
      AppUI.set('worksheetGridScrollPosition', scrollPosition);
    }
    if (AppUI.layout.isMobile && AppUI.siteIsMemrev && window.scrollY > AppUI.layout.viewportHeight) {
      this.showBackToTop = true;
    } else if (this.showBackToTop) {
      this.showBackToTop = false;
    }
  }

  componentDidMount() {
    AppUI.layout.updateDimensions();
    window.addEventListener('scroll', this.onScroll, true);
    polyfillCountryFlagEmojis();
  }

  componentDidUpdate() {
    const routeName = this.currentRouteName;
    if (routeName && routeName !== this.previousRouteName) {
      this.previousRouteName = routeName;
      AppUI.set('clickedLinkId', null);
      const el = document.getElementsByClassName('app')[0];
      const routes = this.currentRoutes;
      if (this.currentRouteIsAdminArea && !AppUI.user.signedOut && (!AppUI.userIsLoaded || !AppUI.user.anyAdmin)) {
        AppUI.routeStore.routeToNamed('landing');
      } else if (AppUI.siteIsMemrev && !some(routes, 'allowMemrev')) {
        AppUI.routeStore.routeToNamed(this.memrevHomeRoute);
      } else if (AppUI.siteIsSeedlang && some(routes, 'blockSeedlang')) {
        // this is safe because Easy German was the only accessible site through Seedlang
        window.location.assign(window.location.href.replace('seedlang.com', 'members.easygerman.org'));
        return;
      }
      if (el && routeName === 'stories.home' && isPresent(AppUI.contentTreeScrollPosition)) {
        el.scrollTop = AppUI.contentTreeScrollPosition;
      } else if (el && ['videos.index', 'podcast.index', 'worksheets.index'].includes(routeName)) {
        el.scrollTop = AppUI.worksheetGridScrollPosition;
      } else if (el && (routeName === 'memberships.index' || routeName === 'nouns.trainer' || routeName === 'vocab.home')) {
        el.scrollTop = 0;
      }
      AppUI.onNewRoute();
      this.redirectSignedOutFromProtectedRoute();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  }

  @computed get useMobileHeader() {
    return AppUI.layout.viewportWidth < 730;
  }

  @computed get migrationBannerContent() {
    if (AppUI.siteIsSeedlang || this.hideHeader) return null;
    if (!AppUI.site?.patreonClientId) return null;
    const { routeName } = AppUI.routeStore;
    if (!routeName || routeName.startsWith('settings')) return null;

    const hasMembershipType = !!AppUI.user.currentGroupMembershipType;
    const isPatreon = AppUI.user.currentUserGroup?.isPatreon;
    const hasNonPatreonMembershipType = hasMembershipType && !isPatreon;

    if (AppUI.isPaymentMigrationStartable && !hasNonPatreonMembershipType) {
      return {
        to: 'onboarding.migration',
        text: <>You are currently subscribed via Patreon. To unlock all interactive features, please <u>migrate your payment</u> to our platform.</>,
      };
    }

    if (AppUI.user.paymentMigration?.status === 'waiting_for_patreon_cancellation') {
      return {
        to: 'onboarding.migration',
        text: <>Thank you for migrating your payment method. ⚠️ Don't forget to <u>cancel your subscription</u> on Patreon to avoid being charged twice!</>,
      };
    }

    if (!AppUI.site?.showPatreonSignInBanner) {
      return null;
    }

    const isHomePage = routeName === AppUI.site.signedOutHomePageRoute;
    const isMembershipsPage = routeName === 'memberships.index';
    const showOnHomePage = isHomePage && !AppUI.userIsSignedInAndNotGuest;
    const showOnMembershipsPage = isMembershipsPage && !AppUI.user.isMember;

    if (showOnHomePage || showOnMembershipsPage) {
      return {
        to: 'onboarding.patreon',
        text: <>Already a member on Patreon? <u>Sign In here</u></>,
      };
    }
    return null;
  }

  renderContent() {
    if (!this.userIsLoaded || this.landingRouteShouldRedirect) {
      return (
        <Spinner />
      );
    }
    if (this.userIsLoaded) {
      return (
        <Wrapper
          display='flex'
          ref={this.appRef}
          className='app'
          visibility={AppUI.loadingUser ? 'hidden' : 'visible'}
          width={AppUI.layout.isMobile ? pixify(AppUI.layout.viewportWidth) : '100%'}
          overflow={this.wrapperOverflow}
        >
          {
            AppUI.layout.isMobile && AppUI.siteIsMemrev && this.showBackToTop &&
            <BackToTopButton
              floating
              background={AppUI.site.accentColor}
              right='5px'
              bottom='40px'
              onClick={() => AppUI.scrollToPageTop('smooth', 'top')}
            />
          }
          <MissingAgreement />
          <UpdatedTermsModal />
          <span id='top' />
          <MobileOnly>
            <OverlayWrapper />
          </MobileOnly>
          {
            AppUI.siteIsSeedlang && !this.useMobileHeader && !this.hideHeader &&
            <SeedlangDesktopHeader
              position={this.showLandingPage ? 'absolute' : 'fixed'}
              background={this.showLandingPage ? 'transparent' : Theme.siteHeaderColor}
              currentRoute={this.currentRouteName}
              signedIn={AppUI.user.signedIn}
            />
          }
          {
            AppUI.siteIsMemrev && !this.hideHeader &&
            <MemrevHeader
              position={AppUI.layout.isMobile ? 'static' : 'fixed'}
              background={AppUI.user?.site?.headerBackgroundColor}
              currentRoute={this.currentRouteName}
              signedIn={AppUI.user.signedIn}
              navigationItems={AppUI.user?.site?.navigationItems}
              languageId={AppUI.user?.site?.languageId}
              logo={AppUI.user?.site?.image}
            />
          }
          {
            this.migrationBannerContent &&
            <MigrationBanner>
              <Link to={this.migrationBannerContent.to}>
                {this.migrationBannerContent.text}
              </Link>
            </MigrationBanner>
          }
          {
            AppUI.siteIsSeedlang && this.useMobileHeader && !this.hideHeader &&
            <MobileHeader
              currentRoute={this.currentRouteName}
              signedIn={AppUI.user.signedIn}
            />
          }
          <MainContainer
            maxWidth={this.showFullWidth ? null : '1180px'}
            padding={this.mainContainerPadding}
            overflow={this.mainContainerOverflow}
            mobileFooterVisible={this.showMobileFooter}
            isMobile={AppUI.layout.isMobile}
          >
            {
              !this.deckRoute &&
              <BannerNoticeIndex />
            }
            <Outlet />
          </MainContainer>
          {
            this.showMobileFooter &&
            <MobileFooter
              currentRoute={this.currentRouteName}
              bgColor='#fff'
            />
          }
          {
            (!AppUI.layout.isMobile || !AppUI.site.isSeedlang) && !this.hideFooter &&
            <DesktopFooter
              bgColor={AppUI.user?.site?.footerBackgroundColor}
            />
          }
          {
            (['localhost', 'ngrok'].some(str => window.location.hostname.includes(str)) && this.showLocalhostBar) &&
            <Localhost
              onClick={() => this.showLocalhostBar = false}
            >
              <div>
                localhost
              </div>
            </Localhost>
          }
          {
            window.location.hostname === 'localhost' &&
            <ServerAlert />
          }
          <CookieAgreement />
        </Wrapper>
      );
    }
  }

  render() {
    return (
      <MantineProvider
        theme={mantineTheme}
        stylesTransform={emotionTransform}
        cssVariablesResolver={v8CssVariablesResolver}
      >
        <MantineEmotionProvider>
          {this.renderContent()}
        </MantineEmotionProvider>
      </MantineProvider>
    );
  }
}

export default App;
